import React, { useMemo } from 'react';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Box from '@material-ui/core/Box';
import cx from 'classnames';

import IKImage from '../IKImage/IKImage';
import useStyles from './styles';
import { Navigation, NavigationItemType, NavigationSection } from '../../types';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import InstagramIcon from '../../assets/icons/InstagramIcon';
import TwitterIcon from '../../assets/icons/TwitterIcon';
import FacebookIcon from '../../assets/icons/FacebookIcon';
import { colorsPalette } from '../../theme';

interface FooterProps {
  footerPartners: FooterPartnersType[];
  navigation?: Navigation;
  tenantTitle?: string | null;
  linkedinLink?: string | null;
  twitterLink?: string | null;
  facebookLink?: string | null;
  instagramLink?: string | null;
}

interface FooterPartnersType {
  id: number | string;
  image_url: string;
  title: string;
}

const Footer: React.FC<FooterProps> = ({
  footerPartners,
  navigation,
  tenantTitle,
  linkedinLink,
  twitterLink,
  facebookLink,
  instagramLink,
}) => {
  const classes = useStyles();
  const year = new Date().getFullYear();

  const navigationSections: NavigationSection[] = useMemo(() => {
    const sections = navigation?.items
      ? (navigation.items.filter(
          (item) => item.item_type === NavigationItemType.Section,
        ) as NavigationSection[])
      : [];

    return sections.map((section) => ({
      ...section,
      items: section?.items ? section.items.filter((item) => item?.type === 'Link') : [],
    }));
  }, [navigation]);

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.footerContainer} component="footer">
        <Box className={classes.footer}>
          {footerPartners && (
            <Box>
              <Typography component="h3" className={classes.footerTitle}>
                Accredited by
              </Typography>
              <Box className={classes.footerPartners}>
                {footerPartners.map((item) => (
                  <Box className={cx(classes.footerPartner, 'footer-partner')} key={item.id}>
                    <IKImage
                      transformation={[{ height: 52 }]}
                      path={item.image_url}
                      draggable={false}
                      alt={item.title}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box className={classes.footerNavigation}>
            {navigationSections.map((block) => (
              <Box key={block.title} className={classes.footerLinksBlock}>
                <Typography component="h3" className={classes.footerTitle}>
                  {block.title}
                </Typography>
                {block.items &&
                  block.items.map((item) => (
                    <Link key={item?.url} href={item?.url} className={classes.footerLink}>
                      {item?.title}
                    </Link>
                  ))}
              </Box>
            ))}
          </Box>
        </Box>
        <Box className={classes.bottomFooter}>
          <Box className={classes.socialLinks}>
            {linkedinLink && (
              <a
                className={cx(classes.socialIcon, 'footer-social-linkedin_link')}
                href={linkedinLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="linkedin_link"
              >
                <LinkedInIcon color={colorsPalette.white} />
              </a>
            )}
            {instagramLink && (
              <a
                className={cx(classes.socialIcon, 'footer-social-instagram_link')}
                href={instagramLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="instagram_link"
              >
                <InstagramIcon color={colorsPalette.white} />
              </a>
            )}
            {facebookLink && (
              <a
                className={cx(classes.socialIcon, 'footer-social-facebook_link')}
                href={facebookLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="facebook_link"
              >
                <FacebookIcon color={colorsPalette.white} />
              </a>
            )}
            {twitterLink && (
              <a
                className={cx(classes.socialIcon, 'footer-social-twitter_link')}
                href={twitterLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="twitter_link"
              >
                <TwitterIcon color={colorsPalette.white} />
              </a>
            )}
          </Box>
          <Box className={classes.license}>
            © {tenantTitle} {year}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
